import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  OutagesResponse,
  OutageViewModel,
  OutageViewModelWithContacts,
} from "../../../api/generated/models";
import { IStandardError } from "../../../shared/types/error.types";

export interface ISubmittedOutage {
  isSubmitted: boolean;
  outage: OutageViewModel | null;
  submitOutageError: IStandardError | null;
}
export interface ISearchedOutages {
  outageCollection: Array<OutageViewModel> | null;
  searchedOutagesError: IStandardError | null;
}
export interface IAdminOutages {
  outageCollection: Array<OutageViewModelWithContacts> | null;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number | null;
  };
  adminOutagesError: IStandardError | null;
  updateAdminOutagesError: IStandardError | null;
  isLoading: boolean;
}

export interface IImageUploadState {
  imgDataUrl?: string | ArrayBuffer | null;
  fileData?: Blob | null;
  imageProcessing: boolean;
  showSafetyTooltip: boolean;
}

export interface IOutagesState {
  outageCollection: Array<OutageViewModel>;
  submittedOutage: ISubmittedOutage;
  searchedOutages: ISearchedOutages;
  adminOutages: IAdminOutages;
  isLoading: boolean;
  outagesError: IStandardError | null;
  imageUpload: IImageUploadState;
}

/**
 * --------------------------------------------------------------------------
 *  INITIAL STATE
 * --------------------------------------------------------------------------
 */
const initialState: IOutagesState = {
  outageCollection: [],
  submittedOutage: {
    isSubmitted: false,
    outage: null,
    submitOutageError: null,
  },
  searchedOutages: {
    outageCollection: null,
    searchedOutagesError: null,
  },
  adminOutages: {
    outageCollection: null,
    pagination: {
      currentPage: 1,
      pageSize: 10,
      totalCount: null,
    },
    adminOutagesError: null,
    updateAdminOutagesError: null,
    isLoading: false,
  },
  imageUpload: {
    imgDataUrl: null,
    fileData: null,
    imageProcessing: false,
    showSafetyTooltip: false,
  },
  isLoading: false,
  outagesError: null,
};

/**
 * --------------------------------------------------------------------------
 *  STATE SLICE
 * --------------------------------------------------------------------------
 */
const outagesSlice = createSlice({
  name: "outages",
  initialState,
  reducers: {
    getOutagesSuccess(state, action: PayloadAction<OutagesResponse>) {
      state.outageCollection = action.payload;
      state.outagesError = null;
      state.isLoading = false;
    },
    getOutagesFailed(state, action: PayloadAction<OutagesResponse>) {
      const error = {
        ...action.payload,
      };
      state.outageCollection = [];
      state.outagesError = error;
      state.isLoading = false;
    },
    submitOutagesSuccess(state, action: PayloadAction<OutageViewModel>) {
      const data = action.payload;
      state.submittedOutage = {
        isSubmitted: true,
        outage: data,
        submitOutageError: null,
      };
      state.isLoading = false;
    },
    submitOutagesFailed(state, action: PayloadAction<OutagesResponse>) {
      const error = action.payload;
      state.submittedOutage = {
        isSubmitted: false,
        outage: null,
        submitOutageError: error,
      };
      state.isLoading = false;
    },
    searchOutagesSuccess(state, action: PayloadAction<OutagesResponse>) {
      state.searchedOutages.outageCollection = action.payload.results;
      state.searchedOutages.searchedOutagesError = null;
      state.isLoading = false;
    },
    searchOutagesFailed(state, action: PayloadAction<OutagesResponse>) {
      const error = action.payload;
      state.searchedOutages = {
        outageCollection: null,
        searchedOutagesError: error,
      };
      state.isLoading = false;
    },
    adminOutagesSuccess(state, action: PayloadAction<OutagesResponse>) {
      const { currentPage, outageViewModelWithContacts, pageSize, totalCount } =
        action.payload.results;

      state.adminOutages = {
        ...state.adminOutages,
        outageCollection: outageViewModelWithContacts,
        pagination: {
          currentPage,
          pageSize,
          totalCount,
        },
        adminOutagesError: null,
        isLoading: false,
      };
    },
    adminOutagesFailed(state, action: PayloadAction<OutagesResponse>) {
      const error = action.payload;
      state.adminOutages = {
        ...state.adminOutages,
        outageCollection: null,
        adminOutagesError: error,
        isLoading: false,
      };
    },
    updateAdminOutagesFailed(state, action: PayloadAction<OutagesResponse>) {
      const error = action.payload;
      state.adminOutages = {
        ...state.adminOutages,
        updateAdminOutagesError: error,
        isLoading: false,
      };
    },
    setIsAdminLoading(state, action: PayloadAction<boolean>) {
      state.adminOutages.isLoading = action.payload;
    },
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setImageProcessing(state, action: PayloadAction<boolean>) {
      state.imageUpload.imageProcessing = action.payload;
    },
    setImageDataUrl(
      state,
      action: PayloadAction<string | ArrayBuffer | null | undefined>
    ) {
      state.imageUpload.imgDataUrl = action.payload;
    },
    setFileData(state, action: PayloadAction<any>) {
      state.imageUpload.fileData = action.payload;
    },
    setShowSafetyTooltip(state, action: PayloadAction<boolean>) {
      state.imageUpload = {
        ...state.imageUpload,
        showSafetyTooltip: action.payload,
      };
    },
    clearImageData(state) {
      state.imageUpload = {
        imageProcessing: false,
        fileData: null,
        imgDataUrl: null,
        showSafetyTooltip: false,
      };
    },
    clearOutageError(state) {
      state.outagesError = null;
      state.isLoading = false;
    },
    clearSubmittedOutage(state) {
      state.submittedOutage = {
        isSubmitted: false,
        outage: null,
        submitOutageError: null,
      };
      state.isLoading = false;
    },
    clearSearchedOutages(state) {
      state.searchedOutages = {
        outageCollection: null,
        searchedOutagesError: null,
      };
      state.isLoading = false;
    },
  },
});

/**
 * --------------------------------------------------------------------------
 *  ACTIONS
 * --------------------------------------------------------------------------
 */
export const {
  getOutagesSuccess,
  getOutagesFailed,
  searchOutagesSuccess,
  searchOutagesFailed,
  adminOutagesSuccess,
  adminOutagesFailed,
  updateAdminOutagesFailed,
  setIsAdminLoading,
  submitOutagesSuccess,
  submitOutagesFailed,
  setIsLoading,
  setImageDataUrl,
  setFileData,
  setImageProcessing,
  setShowSafetyTooltip,
  clearImageData,
  clearOutageError,
  clearSubmittedOutage,
  clearSearchedOutages,
} = outagesSlice.actions;

export default outagesSlice.reducer;
